import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, FileText, PenTool, Youtube,
  TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles, Minus, Plus, Divide, Hash
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  NT01: {
    scene: '🌡️ 겨울 아침. 민준이가 날씨 앱을 보다가 이상한 걸 발견했다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '소연아, 오늘 기온이 영하 7도래. 근데 어제는 영상 3도였잖아. 온도 차이가 얼마야?' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '3 빼기 -7이니까... 음, 헷갈리는데? 그냥 7 + 3 = 10도 차이 아닐까?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '근데 마이너스가 붙으면 어떻게 계산해야 해?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '좋은 질문이에요! "영하"가 바로 음수(-7)예요. 온도뿐 아니라 빚, 지하층, 해수면 아래 깊이도 음수로 표현해요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '아, 음수가 실생활에 이렇게 많이 쓰이는구나! 그럼 -7과 +3을 수직선에 놓으면 어떻게 될까요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '수직선에서 0을 기준으로 오른쪽이 양수(+), 왼쪽이 음수(-)예요. -7은 0에서 왼쪽으로 7칸, +3은 오른쪽으로 3칸이죠!' },
    ],
    reveal: '양수(+) = 0보다 큰 수 | 음수(-) = 0보다 작은 수 | 정수 = 양의 정수 + 0 + 음의 정수',
    revealEmoji: '↔️',
  },
  NT02: {
    scene: '📏 체육 시간. 선생님이 수직선 달리기 게임을 한다.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '자, 운동장 중앙이 0이에요. 동쪽으로 5m 가면 +5, 서쪽으로 3m 가면 -3이라고 써요.' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '그럼 절댓값이 뭐예요? 수학책에서 봤는데 |−5| 이런 거요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '절댓값은 수직선에서 0까지의 거리예요. |-5|=5, |+3|=3. 방향은 무시하고 크기만 보는 거죠!' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '아~ 그러면 -5와 +5는 부호는 반대지만 절댓값이 같아서 0으로부터 같은 거리에 있겠네요!' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '절댓값이 클수록 0에서 멀리 있는 거고, 음수끼리 비교할 때는 절댓값이 작은 쪽이 더 큰 수겠다!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '수직선과 절댓값만 이해하면 정수의 대소 비교가 자연스럽게 됩니다.' },
    ],
    reveal: '|a| = a가 수직선에서 0까지의 거리 | |-5|=5, |+3|=3 | 음수는 절댓값이 클수록 더 작은 수!',
    revealEmoji: '📏',
  },
  NT03: {
    scene: '🏦 민준이네 아버지가 가계부를 정리하고 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '아빠, 가계부에 플러스랑 마이너스가 섞여 있어요. 어떻게 순서대로 정리해요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '(다음 날 수업에서) 정수의 대소 비교! 수직선에서 오른쪽에 있을수록 큰 수예요.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '양수는 모두 0보다 크고, 음수는 모두 0보다 작으니까 양수 > 0 > 음수 순서죠?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '근데 음수끼리 비교할 때 -3과 -7 중 어느 게 더 크죠? 7이 3보다 크니까 -7이 더 크나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '반대예요! 수직선에서 -3은 -7보다 오른쪽에 있어요. 그래서 -3 > -7. 절댓값이 작은 음수가 더 크답니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '수직선을 항상 머릿속에 그리세요. 오른쪽 = 더 큰 수. 음수는 절댓값이 작을수록 큽니다.' },
    ],
    reveal: '양수 > 0 > 음수 | 음수끼리: 절댓값 작은 쪽이 더 큼 | -3 > -7 (수직선에서 -3이 오른쪽)',
    revealEmoji: '📊',
  },
  NT04: {
    scene: '🛒 마트에서 영수증을 계산하는 상황.',
    lines: [
      { speaker: 'friend', emoji: '😄', name: '소연', text: '민준아, 나 용돈이 3000원인데 5000원짜리 과자를 사면 -2000원이 되는 거잖아. 이걸 식으로 어떻게 써?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '3000 + (-5000) = -2000? 맞나?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 덧셈에서 양수+음수는 부호가 다르면 절댓값의 차를 구하고, 절댓값이 큰 쪽 부호를 붙여요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '그러면 뺄셈은요? 3000 - (-1000)은 뭐예요? 마이너스 마이너스가 플러스 된다고 들었는데...' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '뺄셈은 뺄 수의 부호를 바꿔서 덧셈으로 바꾸면 돼요! 3000 - (-1000) = 3000 + 1000 = 4000.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '정수 뺄셈 = 덧셈으로 변환. a - b = a + (-b). 이 원리 하나로 모든 뺄셈이 해결됩니다!' },
    ],
    reveal: '같은 부호: 절댓값 더하고 공통 부호 | 다른 부호: 절댓값 차이 + 큰 쪽 부호 | 뺄셈 → 덧셈 변환',
    revealEmoji: '➕',
  },
  NT05: {
    scene: '📉 주식 동아리 발표 시간. 음수 곱셈을 실생활로 배운다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, (-3) × (-4)가 왜 +12예요? 음수 곱하기 음수가 양수가 되는 게 이해가 안 가요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '주식으로 생각해봐요. 주가가 하루에 3씩 내려가면(-3), 3일 전(-3)에는 얼마였을까요?' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '3일 전이니까 더 높았겠죠? (-3) × (-3) = +9 오른거네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 부호 규칙: 같은 부호끼리 곱하면 양수, 다른 부호면 음수. (+)×(+)=+, (-)×(-)=+, (+)×(-)=-, (-)×(+)=-' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '나눗셈도 같은 규칙이에요? (-12) ÷ (-4) = +3?' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '곱셈·나눗셈 부호 규칙은 동일! 같은 부호 → 양수, 다른 부호 → 음수. 절댓값은 그냥 곱하거나 나누면 됩니다.' },
    ],
    reveal: '같은 부호 × = + | 다른 부호 × = - | 나눗셈도 동일 규칙 | (-) × (-) = +',
    revealEmoji: '✖️',
  },
  NT06: {
    scene: '🍕 피자를 공평하게 나누는 상황. 분수와 소수의 세계로!',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, 유리수가 정수랑 어떻게 달라요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '유리수는 분수 a/b (b≠0, a·b는 정수) 꼴로 나타낼 수 있는 수예요. 정수도 사실 분수잖아요 — 3 = 3/1!' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '그럼 1/2, -3/4, 0.5도 유리수네요? 소수도 분수로 나타낼 수 있으니까요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 유리수의 분류: 양의 유리수(+분수) / 0 / 음의 유리수(-분수). 정수는 유리수의 일부예요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '그럼 양의 유리수에는 양의 정수(자연수)도 포함되고 양의 분수도 있는 거죠?' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '자연수 ⊂ 정수 ⊂ 유리수. 집합 관계를 그림으로 그리면 포함 관계가 한눈에 보입니다.' },
    ],
    reveal: '유리수 = a/b 꼴 (b≠0) | 정수 ⊂ 유리수 | 양의 유리수 / 0 / 음의 유리수로 분류',
    revealEmoji: '🔢',
  },
  NT07: {
    scene: '🏧 ATM기 앞. 잔액을 계산하며 유리수 사칙연산 총정리.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '통장에 12500원 있는데 세 번에 걸쳐 각각 (-3400), (+2000), (-1500)원이 생겼어. 최종 잔액은?' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '12500 + (-3400) + 2000 + (-1500)이니까... 덧셈·뺄셈 먼저 교환법칙으로 정리하면 되겠다!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '혼합 계산 순서: ① 괄호 ② 곱셈·나눗셈 ③ 덧셈·뺄셈. 같은 우선순위면 왼쪽부터!' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '12500 - 3400 + 2000 - 1500 = 9600원! 계산 순서를 지키니까 확실하네요.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '혼합계산에서 분배법칙도 쓸 수 있어요. (-3) × (5 - 2) = (-3)×5 + (-3)×(-2) = -15 + 6 = -9!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '혼합계산 순서 + 분배법칙 활용이 핵심. 부호를 빠뜨리지 않도록 한 단계씩 천천히 계산하세요.' },
    ],
    reveal: '혼합계산 순서: 괄호 → 곱·나눗 → 덧·뺄 | 분배법칙 a(b+c)=ab+ac | 부호 실수 NO!',
    revealEmoji: '🧮',
  },
  NT08: {
    scene: '🌱 수포자 민준이. 선생님과 1:1로 처음부터 시작한다.',
    lines: [
      { speaker: 'student', emoji: '😞', name: '민준', text: '선생님... 정수, 유리수, 절댓값... 다 어려워요. 어디서부터 다시 시작해야 할지 모르겠어요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '괜찮아요! 딱 하나만 기억해요: 수직선. 0을 중심으로 오른쪽이 양수, 왼쪽이 음수. 여기서 출발해요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '-3 + (-2)는... 수직선에서 -3에서 왼쪽으로 2칸 더 가면 -5? 맞나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 수직선으로 생각하면 틀릴 수가 없어요. 덧셈은 오른쪽, 뺄셈은 왼쪽이에요.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '민준아, 나도 처음엔 어려웠어. 수직선 그림 하나면 충분해! 부호도 그냥 방향이야 — 양수=오른쪽, 음수=왼쪽' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '수직선 하나로 정수의 모든 개념이 연결됩니다. 처음이 어려울수록 기본부터 천천히!' },
    ],
    reveal: '수직선 = 정수·유리수의 지도 | 덧셈=오른쪽 이동, 뺄셈=왼쪽 이동 | 천천히 해도 맞으면 된다',
    revealEmoji: '🌱',
  },
}

// ── STEPS 데이터 ─────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'NT01',
    order: 1,
    title: '양수·음수·정수의 개념',
    subtitle: '0보다 큰 수와 작은 수, 정수를 이해한다',
    difficulty: 'beginner' as const,
    teaches: ['PositiveNumber', 'NegativeNumber', 'Integer'],
    icon: '↔️',
    color: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600', btn: 'bg-blue-500' },
    objective: '양수·음수·정수의 뜻을 이해하고, 실생활 상황을 양수·음수로 표현할 수 있다.',
    concept: `정수(Integer)란 양의 정수, 0, 음의 정수를 모두 합친 수의 집합입니다.

📌 핵심 개념
• 양의 정수(자연수): +1, +2, +3, … (보통 +를 생략하여 1, 2, 3으로 표기)
• 음의 정수: -1, -2, -3, …
• 0: 양수도 음수도 아닌 수

📌 실생활에서 음수가 쓰이는 상황
• 온도: 영하 5도 → -5°C
• 해발고도: 해수면 아래 200m → -200m
• 잔액: 빚 3000원 → -3000원
• 엘리베이터: 지하 2층 → -2층

📌 수직선(Number Line)
←  -5  -4  -3  -2  -1  0  +1  +2  +3  +4  +5  →
• 0을 기준으로 오른쪽 → 양수, 왼쪽 → 음수
• 오른쪽으로 갈수록 큰 수

⚠️ 수포자 탈출 포인트
음수는 "빚"이나 "온도 영하"처럼 실생활로 이해하면 쉬워요!
"어떤 수의 반대" = 부호가 반대인 수 (3의 반대 → -3)`,
    examples: [
      { q: '다음을 양수 또는 음수로 나타내세요: 해발 1500m, 영하 20도, 빚 5000원', a: '+1500, -20, -5000', detail: '• 해발 1500m → 해수면 위 → 양수 → +1500\n• 영하 20도 → 0°C 아래 → 음수 → -20\n• 빚 5000원 → 부족한 것 → 음수 → -5000' },
      { q: '-8보다 크고 +3보다 작은 정수를 모두 쓰세요.', a: '-7, -6, -5, -4, -3, -2, -1, 0, 1, 2', detail: '-8보다 크고 +3보다 작은 수:\n-7, -6, -5, -4, -3, -2, -1, 0, 1, 2\n(총 10개, -8과 +3 자체는 포함 안 됨)' },
    ],
    keyPoints: ['양수: 0보다 큰 수', '음수: 0보다 작은 수', '정수 = 양의 정수 + 0 + 음의 정수'],
    practice: {
      q: '다음 상황을 정수로 나타내고, 수직선 위에 표시하세요.\n① 지상 3층 → ___  ② 지하 4층 → ___  ③ 해수면과 같은 높이 → ___',
      a: '① +3  ② -4  ③ 0',
      hint: '기준(0)을 정하고, 기준보다 위/많으면 양수, 아래/적으면 음수로 표현하세요.',
      solutionSteps: [
        { label: '① 지상 3층', content: '지상 = 기준(0)보다 위 → 양수 → +3' },
        { label: '② 지하 4층', content: '지하 = 기준(0)보다 아래 → 음수 → -4' },
        { label: '③ 해수면', content: '기준과 같은 높이 → 0' },
        { label: '수직선 위치', content: '←  -4  ...  0  ...  +3  →\n-4는 0에서 왼쪽 4칸, +3은 오른쪽 3칸' },
      ]
    },
    misconceptions: [
      { code: 'NM01', desc: '0을 양수라고 생각한다 → 0은 양수도 음수도 아닙니다!' },
      { code: 'NM02', desc: '-0 = 0이 아니라 따로 있다고 생각한다 → -0 = 0, 0은 하나뿐!' },
    ],
  },
  {
    id: 'NT02',
    order: 2,
    title: '수직선과 절댓값',
    subtitle: '수직선에서 수의 위치를 파악하고 절댓값을 구한다',
    difficulty: 'beginner' as const,
    teaches: ['NumberLine', 'AbsoluteValue', 'Distance'],
    icon: '📏',
    color: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', accent: 'text-indigo-600', btn: 'bg-indigo-500' },
    objective: '수직선에서 정수의 위치를 나타내고, 절댓값의 의미를 이해하여 계산할 수 있다.',
    concept: `절댓값(Absolute Value)이란 수직선에서 어떤 수와 원점(0) 사이의 거리입니다.

📌 절댓값 표기와 계산
• |a| 기호로 나타냄
• |+5| = 5  (0에서 오른쪽으로 5칸)
• |-5| = 5  (0에서 왼쪽으로 5칸)
• |0| = 0   (0은 원점이므로 거리 0)

📌 핵심 성질
① |a| ≥ 0  (절댓값은 항상 0 이상)
② |a| = |-a|  (부호가 달라도 절댓값은 같음)
③ a > 0 이면 |a| = a
   a = 0 이면 |a| = 0
   a < 0 이면 |a| = -a  (음수의 절댓값 = 그 수에 -를 붙인 것)

📌 수직선에서 두 수 사이의 거리
두 수 a, b 사이의 거리 = |a - b| = |b - a|

예) -3과 +5 사이의 거리 = |-3 - 5| = |-8| = 8
    또는 |5 - (-3)| = |8| = 8`,
    examples: [
      { q: '다음 절댓값을 구하세요: |−7|, |+4|, |0|, |−3/2|', a: '7, 4, 0, 3/2', detail: '• |−7| = 7  (0에서 왼쪽 7칸 거리)\n• |+4| = 4  (0에서 오른쪽 4칸 거리)\n• |0| = 0   (원점이므로 거리 0)\n• |−3/2| = 3/2  (부호만 제거)' },
      { q: '절댓값이 4인 수를 모두 구하세요.', a: '+4와 -4', detail: '절댓값이 4 → 0에서 거리가 4인 수\n→ 오른쪽 4칸: +4\n→ 왼쪽 4칸: -4\n(수직선에서 0의 양쪽에 각각 하나씩!)' },
    ],
    keyPoints: ['|a| = 0에서의 거리 (항상 0 이상)', '|-a| = |a|', '절댓값이 클수록 0에서 멀다'],
    practice: {
      q: '① |-9|를 구하세요.\n② 절댓값이 6인 정수를 모두 구하세요.\n③ -4와 +7 사이의 거리를 구하세요.',
      a: '① 9  ② +6, -6  ③ 11',
      hint: '③번은 |(-4) - (+7)|로 계산하세요.',
      solutionSteps: [
        { label: '① |-9|', content: '-9는 0에서 왼쪽 9칸 → 거리 = 9' },
        { label: '② 절댓값이 6인 정수', content: '0에서 거리가 6인 수: +6(오른쪽), -6(왼쪽)' },
        { label: '③ -4와 +7의 거리', content: '|(-4) - (+7)| = |-4 - 7| = |-11| = 11\n또는 수직선에서 세어도: -4에서 +7까지 11칸' },
      ]
    },
    misconceptions: [
      { code: 'NM03', desc: '|-5| = -5라고 계산한다 → 절댓값은 항상 0 이상! |-5| = 5' },
      { code: 'NM04', desc: '절댓값이 크면 항상 더 큰 수라고 생각한다 → -10의 절댓값은 10으로 크지만 -10은 작은 수!' },
    ],
  },
  {
    id: 'NT03',
    order: 3,
    title: '정수의 대소 비교',
    subtitle: '수직선을 이용하여 정수의 크기를 비교한다',
    difficulty: 'beginner' as const,
    teaches: ['Comparison', 'NumberLine', 'Order'],
    icon: '📊',
    color: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', btn: 'bg-emerald-500' },
    objective: '정수의 대소 관계를 수직선과 부호 규칙으로 파악하여 올바르게 비교할 수 있다.',
    concept: `정수의 대소 비교 — 수직선에서 오른쪽에 있는 수가 항상 더 큽니다!

📌 기본 규칙
① 양수 > 0 > 음수
   (모든 양수는 0보다 크고, 0은 모든 음수보다 크다)

② 양수끼리: 절댓값이 큰 쪽이 더 크다
   +3 < +7  (수직선에서 +7이 오른쪽)

③ 음수끼리: 절댓값이 작은 쪽이 더 크다 ← 핵심!
   -3 > -7  (-3이 수직선에서 오른쪽)

📌 수직선으로 확인
←  -7  -6  -5  -4  -3  -2  -1  0  1  2  3  →
    왼쪽 ←(작다)                  오른쪽 →(크다)

📌 세 수 이상 비교: 부등호 연결
예) -5, +2, -1을 작은 순서로: -5 < -1 < +2
    (수직선에서 왼쪽부터 나열)

⚠️ 가장 흔한 실수
-9 > -3이라고 잘못 생각하는 것!
→ 9 > 3은 맞지만, -9 < -3 (음수는 반대!)`,
    examples: [
      { q: '다음 수를 작은 것부터 큰 순서로 나열하세요: -3, +5, 0, -7, +2', a: '-7 < -3 < 0 < +2 < +5', detail: '수직선에서 왼쪽부터 나열:\n-7 (가장 왼쪽) < -3 < 0 (중앙) < +2 < +5 (가장 오른쪽)' },
      { q: '□ 안에 알맞은 부등호를 써넣으세요: -8 □ -3, +5 □ -100', a: '-8 < -3, +5 > -100', detail: '• -8 □ -3: 음수끼리. |-8|=8 > |-3|=3이므로 -8이 더 작음 → -8 < -3\n• +5 □ -100: 양수 > 음수 → +5 > -100' },
    ],
    keyPoints: ['오른쪽 = 더 큰 수 (수직선)', '양수 > 0 > 음수', '음수: 절댓값 작을수록 큼'],
    practice: {
      q: '다음 수 중에서\n① 가장 큰 수: -1, -10, 0, +3, -5\n② 가장 작은 수: -1, -10, 0, +3, -5',
      a: '① +3  ② -10',
      hint: '수직선을 그려서 가장 오른쪽 / 가장 왼쪽을 찾아보세요.',
      solutionSteps: [
        { label: '수직선 배치', content: '←  -10  -5  -1  0  +3  →' },
        { label: '① 가장 큰 수', content: '수직선에서 가장 오른쪽 → +3' },
        { label: '② 가장 작은 수', content: '수직선에서 가장 왼쪽 → -10' },
      ]
    },
    misconceptions: [
      { code: 'NM05', desc: '절댓값이 크면 항상 크다고 생각한다 → 음수는 절댓값 클수록 더 작다! -9 < -1' },
      { code: 'NM06', desc: '0이 음수보다 작다고 생각한다 → 0은 모든 음수보다 크다! 0 > -100' },
    ],
  },
  {
    id: 'NT04',
    order: 4,
    title: '정수·유리수의 덧셈·뺄셈',
    subtitle: '부호를 고려하여 정수와 유리수를 더하고 뺀다',
    difficulty: 'intermediate' as const,
    teaches: ['Addition', 'Subtraction', 'SignRule'],
    icon: '➕',
    color: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700', accent: 'text-teal-600', btn: 'bg-teal-500' },
    objective: '정수와 유리수의 덧셈·뺄셈을 부호 규칙에 따라 정확하게 계산할 수 있다.',
    concept: `정수의 덧셈 — 부호에 따라 두 가지 경우!

📌 덧셈 규칙
① 같은 부호끼리 더하기
   → 절댓값을 더하고, 공통 부호를 붙인다
   (+5) + (+3) = +(5+3) = +8
   (-5) + (-3) = -(5+3) = -8

② 다른 부호끼리 더하기
   → 절댓값의 차를 구하고, 절댓값이 큰 쪽 부호를 붙인다
   (+7) + (-4) = +(7-4) = +3  (7이 더 크므로 양수)
   (+3) + (-8) = -(8-3) = -5  (8이 더 크므로 음수)

📌 뺄셈 규칙 — 덧셈으로 변환!
   a - b = a + (-b)
   뺄 수의 부호를 바꿔서 덧셈으로!

   (+5) - (+3) = (+5) + (-3) = +2
   (+5) - (-3) = (+5) + (+3) = +8  ← "마이너스 마이너스 = 플러스"
   (-5) - (-3) = (-5) + (+3) = -2

⚠️ 수포자 탈출
뺄셈이 헷갈리면 무조건 "덧셈으로 바꾸기"!
a - b → a + (b의 반대)`,
    examples: [
      { q: '계산하세요: (-8) + (+3)', a: '-5', detail: '다른 부호끼리:\n|-8| = 8, |+3| = 3 → 차 = 8 - 3 = 5\n절댓값이 큰 쪽(-8) 부호 → 음수\n→ -5' },
      { q: '계산하세요: (-4) - (-9)', a: '+5', detail: '뺄셈 → 덧셈 변환:\n(-4) - (-9) = (-4) + (+9)\n다른 부호: |9| - |4| = 5, +9가 더 크므로 양수\n→ +5' },
    ],
    keyPoints: ['같은 부호: 절댓값 더하기 + 공통 부호', '다른 부호: 절댓값 차이 + 큰 쪽 부호', '뺄셈 = 덧셈으로 변환'],
    practice: {
      q: '다음을 계산하세요.\n① (+6) + (-10)  ② (-3) + (-7)  ③ (-5) - (-8)  ④ 3/4 - 5/4',
      a: '① -4  ② -10  ③ +3  ④ -2/4 = -1/2',
      hint: '③은 (-5) + (+8)로 변환하세요. ④는 분모가 같으므로 분자끼리 계산!',
      solutionSteps: [
        { label: '① (+6) + (-10)', content: '다른 부호: |10|-|6|=4, -10이 절댓값 크므로 → -4' },
        { label: '② (-3) + (-7)', content: '같은 부호(음수): 3+7=10, 음수 → -10' },
        { label: '③ (-5) - (-8)', content: '→ (-5) + (+8) = +(8-5) = +3' },
        { label: '④ 3/4 - 5/4', content: '분모 같음: (3-5)/4 = -2/4 = -1/2' },
      ]
    },
    misconceptions: [
      { code: 'NM07', desc: '(-3) - (-5) = -8이라고 계산한다 → 뺄셈은 덧셈 변환! (-3)+( +5) = +2' },
      { code: 'NM08', desc: '다른 부호 덧셈에서 절댓값을 더한다 → 다른 부호면 절댓값의 차!' },
    ],
  },
  {
    id: 'NT05',
    order: 5,
    title: '정수·유리수의 곱셈·나눗셈',
    subtitle: '부호 규칙을 적용하여 곱하고 나눈다',
    difficulty: 'intermediate' as const,
    teaches: ['Multiplication', 'Division', 'SignRule', 'Reciprocal'],
    icon: '✖️',
    color: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600', btn: 'bg-purple-500' },
    objective: '정수와 유리수의 곱셈·나눗셈에서 부호 규칙을 적용하여 정확하게 계산할 수 있다.',
    concept: `곱셈·나눗셈 부호 규칙 — 단 하나의 규칙!

📌 부호 규칙 (곱셈·나눗셈 동일)
  같은 부호 × or ÷ → 결과는 양수(+)
  다른 부호 × or ÷ → 결과는 음수(-)

  (+) × (+) = +   |   (+) ÷ (+) = +
  (-) × (-) = +   |   (-) ÷ (-) = +
  (+) × (-) = -   |   (+) ÷ (-) = -
  (-) × (+) = -   |   (-) ÷ (+) = -

📌 여러 수의 곱셈
  음수 인자의 개수가 짝수 → 결과 양수
  음수 인자의 개수가 홀수 → 결과 음수
  예) (-1)×(-2)×(-3) = -(1×2×3) = -6  (음수 3개 = 홀수 → 음수)

📌 나눗셈 — 역수를 이용한 곱셈으로 변환
  a ÷ b = a × (1/b)  (b의 역수를 곱함)
  예) (-6) ÷ (3/2) = (-6) × (2/3) = -4

📌 역수(Reciprocal)
  a/b의 역수 = b/a (분자·분모를 바꿈)
  예) 2/3의 역수 = 3/2, (-4)의 역수 = -1/4`,
    examples: [
      { q: '계산하세요: (-3) × (+4) × (-2)', a: '+24', detail: '음수 인자: -3, -2 → 2개(짝수) → 결과 양수\n절댓값 곱: 3 × 4 × 2 = 24\n→ +24' },
      { q: '계산하세요: (-3/4) ÷ (9/8)', a: '-2/3', detail: '나눗셈 → 역수 곱셈:\n(-3/4) × (8/9) = -(3×8)/(4×9) = -24/36 = -2/3\n부호: 다른 부호 → 음수' },
    ],
    keyPoints: ['같은 부호 → 양수, 다른 부호 → 음수', '음수 인자 개수: 짝수→+, 홀수→-', '나눗셈 = 역수 곱셈'],
    practice: {
      q: '다음을 계산하세요.\n① (-5) × (-6)  ② (+4) × (-3) × (-1)  ③ (-2/3) ÷ (4/9)',
      a: '① +30  ② +12  ③ -3/2',
      hint: '③은 (-2/3) × (9/4)로 변환한 뒤 계산하세요.',
      solutionSteps: [
        { label: '① (-5) × (-6)', content: '같은 부호(음수×음수) → 양수\n5 × 6 = 30 → +30' },
        { label: '② (+4) × (-3) × (-1)', content: '음수 인자: -3, -1 → 2개(짝수) → 양수\n4 × 3 × 1 = 12 → +12' },
        { label: '③ (-2/3) ÷ (4/9)', content: '역수 변환: (-2/3) × (9/4)\n= -(2×9)/(3×4) = -18/12 = -3/2' },
      ]
    },
    misconceptions: [
      { code: 'NM09', desc: '(-) × (-) = -라고 계산한다 → 같은 부호끼리 곱하면 항상 양수! (-) × (-) = +' },
      { code: 'NM10', desc: '나눗셈에서 역수를 취하지 않는다 → a ÷ b = a × (1/b), 반드시 역수로 바꿔야 함!' },
    ],
  },
  {
    id: 'NT06',
    order: 6,
    title: '유리수의 개념과 분류',
    subtitle: '유리수의 뜻과 분류 체계를 완전히 이해한다',
    difficulty: 'intermediate' as const,
    teaches: ['RationalNumber', 'Classification', 'Fraction', 'Decimal'],
    icon: '🔢',
    color: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-600', btn: 'bg-orange-500' },
    objective: '유리수의 정의와 분류를 이해하고, 자연수·정수·유리수의 포함 관계를 설명할 수 있다.',
    concept: `유리수(Rational Number)란 a/b 꼴(a, b는 정수, b≠0)로 나타낼 수 있는 수입니다.

📌 유리수의 분류
          유리수
         /      \\
     양의 유리수   0   음의 유리수
     /                  \\
 양의 정수(자연수)    음의 정수
 양의 분수(+분수)    음의 분수(-분수)

📌 포함 관계 (집합)
  자연수 ⊂ 정수 ⊂ 유리수
  예) 5는 자연수이면서 정수이면서 유리수 (5 = 5/1)

📌 정수를 유리수로 나타내기
  3 = 3/1 = 6/2 = -6/-2 … (여러 가지 유리수로 표현 가능)
  0 = 0/1 = 0/5 …

📌 유리수와 소수
  유한소수 ↔ 유리수: 0.5 = 1/2, 0.25 = 1/4
  순환소수 ↔ 유리수: 0.333… = 1/3
  (소수도 분수로 나타낼 수 있으면 유리수!)

⚠️ 중요: 정수는 유리수이지만, 유리수가 모두 정수는 아님!
  1/2는 유리수이지만 정수가 아님`,
    examples: [
      { q: '다음 수를 유리수인지 판별하고 분류하세요: -3, 2/5, 0, -1/2, 7', a: '모두 유리수. 분류: -3(음의 정수), 2/5(양의 유리수), 0(0), -1/2(음의 유리수), 7(양의 정수)', detail: '• -3 = -3/1 → 유리수, 음의 정수\n• 2/5 → 유리수, 양의 분수(정수 아님)\n• 0 = 0/1 → 유리수, 0\n• -1/2 → 유리수, 음의 분수\n• 7 = 7/1 → 유리수, 양의 정수(자연수)' },
      { q: '"모든 정수는 유리수이다" — 참인지 거짓인지, 이유와 함께 답하세요.', a: '참. 모든 정수 n은 n/1로 나타낼 수 있으므로 유리수.', detail: '정수 n → n = n/1 (b=1 ≠ 0인 분수)\n→ 유리수 정의 a/b (b≠0) 만족\n따라서 모든 정수는 유리수. (자연수 ⊂ 정수 ⊂ 유리수)' },
    ],
    keyPoints: ['유리수 = a/b 꼴 (b≠0)', '자연수 ⊂ 정수 ⊂ 유리수', '정수도 유리수의 일부'],
    practice: {
      q: '다음 빈칸을 채우세요.\n① 자연수는 정수이다: (O/X)\n② 유리수는 모두 정수이다: (O/X)\n③ -2/3는 (양의 / 음의) 유리수이다.\n④ 정수 -5를 유리수로 나타내면: -5 = ___/1',
      a: '① O  ② X  ③ 음의  ④ -5',
      hint: '② 반례: 1/2는 유리수이지만 정수가 아닙니다.',
      solutionSteps: [
        { label: '① 자연수는 정수', content: '자연수 ⊂ 정수이므로 참 (O)' },
        { label: '② 유리수는 모두 정수?', content: '반례: 1/2는 유리수이지만 정수 아님 → 거짓 (X)' },
        { label: '③ -2/3의 분류', content: '음수 분수 → 음의 유리수' },
        { label: '④ -5를 유리수로', content: '-5 = -5/1 (b=1인 분수 꼴)' },
      ]
    },
    misconceptions: [
      { code: 'NM11', desc: '유리수는 분수만을 의미한다고 생각한다 → 정수도 유리수! 3 = 3/1' },
      { code: 'NM12', desc: '유리수와 정수를 같은 것으로 생각한다 → 정수 ⊂ 유리수, 유리수가 더 넓은 개념!' },
    ],
  },
  {
    id: 'NT07',
    order: 7,
    title: '사칙연산 혼합 계산',
    subtitle: '덧뺄셈·곱나눗셈이 섞인 식을 올바른 순서로 계산한다',
    difficulty: 'advanced' as const,
    teaches: ['MixedOperations', 'OrderOfOperations', 'DistributiveLaw'],
    icon: '🧮',
    color: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', accent: 'text-red-600', btn: 'bg-red-500' },
    objective: '사칙연산이 혼합된 식을 계산 순서에 따라 정확하게 계산할 수 있다.',
    concept: `혼합 계산 순서 — 이 규칙 하나면 끝!

📌 계산 순서 (우선순위 높은 것부터)
  ① 괄호 ( ) 안을 먼저
  ② 곱셈(×) · 나눗셈(÷)
  ③ 덧셈(+) · 뺄셈(-)
  같은 우선순위: 왼쪽부터 오른쪽으로

예) 3 + (-2) × 4 - 6 ÷ (-3)
  = 3 + (-8) - (-2)   ← 곱셈·나눗셈 먼저
  = 3 + (-8) + (+2)   ← 뺄셈 → 덧셈 변환
  = -3

📌 분배법칙 a(b + c) = ab + ac
  (-3) × (5 + (-2)) = (-3)×5 + (-3)×(-2) = -15 + 6 = -9
  또는 괄호 먼저: (-3) × (3) = -9  (같은 결과!)

📌 교환법칙과 결합법칙
  a + b = b + a (교환)
  (a + b) + c = a + (b + c) (결합)
  → 양수끼리, 음수끼리 먼저 더하면 편함!

  예) (-1) + (+5) + (-4) + (+3)
    = (+5 + 3) + (-1 - 4)
    = 8 + (-5) = 3`,
    examples: [
      { q: '계산하세요: (-4) + 3 × (-2) - (-6) ÷ 2', a: '-7', detail: '① 곱셈: 3 × (-2) = -6\n② 나눗셈: (-6) ÷ 2 = -3\n→ (-4) + (-6) - (-3)\n③ 뺄셈→덧셈: (-4) + (-6) + (+3)\n④ 계산: -10 + 3 = -7' },
      { q: '분배법칙으로 계산하세요: (-2) × {3 + (-7)}', a: '+8', detail: '방법1) 괄호 먼저: (-2) × (3-7) = (-2) × (-4) = +8\n방법2) 분배: (-2)×3 + (-2)×(-7) = -6 + 14 = +8\n(두 방법 결과 동일!)' },
    ],
    keyPoints: ['괄호 → 곱·나눗 → 덧·뺄 순서', '같은 우선순위는 왼쪽부터', '분배법칙: a(b+c)=ab+ac'],
    practice: {
      q: '계산하세요.\n① 5 - 3 × (-4) + (-2)\n② (-1/2) × 4 + 3 ÷ (-6)',
      a: '① +15  ② -7/2',
      hint: '①은 곱셈을 먼저, ②는 곱셈과 나눗셈을 모두 먼저 계산하세요.',
      solutionSteps: [
        { label: '① 5 - 3×(-4) + (-2)', content: '곱셈: 3×(-4) = -12\n→ 5 - (-12) + (-2) = 5 + 12 + (-2) = 15' },
        { label: '② (-1/2)×4 + 3÷(-6)', content: '곱셈: (-1/2)×4 = -2\n나눗셈: 3÷(-6) = -1/2\n→ (-2) + (-1/2) = -4/2 + (-1/2) = -5/2\n잠깐, 다시: -2 + (-1/2) = -2 - 1/2 = -5/2 = -7/2? → -4/2 - 1/2 = -5/2\n정정: -2 = -4/2 → -4/2 - 1/2 = -5/2' },
      ]
    },
    misconceptions: [
      { code: 'NM13', desc: '덧셈을 먼저 계산한다 → 곱셈·나눗셈이 먼저! 5 + 3×2 = 5+6 = 11 (11이 맞고 16이 틀림)' },
      { code: 'NM14', desc: '괄호를 마지막에 계산한다 → 괄호가 최우선! 항상 괄호 안부터 시작!' },
    ],
  },
  {
    id: 'NT08',
    order: 8,
    title: '수포자를 위한 처음부터',
    subtitle: '정수와 유리수를 수직선 하나로 완전히 이해한다',
    difficulty: 'beginner' as const,
    teaches: ['ConceptFromScratch', 'AnxietyReduction', 'NumberLine'],
    icon: '🌱',
    color: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', accent: 'text-yellow-600', btn: 'bg-yellow-500' },
    objective: '정수와 유리수의 핵심을 수직선으로 이해하고, 기초 계산에 자신감을 갖는다.',
    concept: `"정수? 유리수? 절댓값? 다 모르겠어요…"라고 느끼나요?
수직선 딱 하나로 시작합시다!

🌱 수직선만 기억하세요

←  -4  -3  -2  -1  0  +1  +2  +3  +4  →
            왼쪽(음수)  |  오른쪽(양수)

① 오른쪽 → 클수록 큰 수 (항상!)
② 0 = 기준점 (양수도 음수도 아님)
③ 절댓값 = 0에서의 거리 (방향 무시)

🧮 덧셈: 수직선에서 이동으로 이해
  (-3) + (+5) = ?
  -3에서 시작 → 오른쪽으로 5칸 이동 → +2

  (-3) + (-2) = ?
  -3에서 시작 → 왼쪽으로 2칸 이동 → -5

🔄 뺄셈: 부호 바꾸고 덧셈
  (-4) - (-2) → (-4) + (+2) = -2
  "뺄셈은 더하기로 바꾸고 부호 반대로"

💡 딱 이것만 기억!
  • 양수: 오른쪽 / 음수: 왼쪽
  • 덧셈: 오른쪽 이동 (양수 더하기) / 왼쪽 이동 (음수 더하기)
  • 뺄셈: 뺄 수의 부호 바꿔서 덧셈`,
    examples: [
      { q: '수직선을 이용해 (-2) + (-3)을 계산해 보세요.', a: '-5', detail: '-2에서 출발 → (-3)이므로 왼쪽으로 3칸 이동\n-2 → -3 → -4 → -5\n정착! → -5\n\n계산: 같은 부호(음수+음수) → 절댓값 합 = 5, 음수 → -5' },
      { q: '(-5) - (-3)을 "부호 바꾸기" 방법으로 계산하세요.', a: '-2', detail: '뺄셈 → 덧셈 변환:\n(-5) - (-3) = (-5) + (+3)\n다른 부호: |5| - |3| = 2, 절댓값 큰 쪽(-5) 부호 → -2\n→ -2' },
    ],
    keyPoints: ['수직선: 오른쪽=크다, 왼쪽=작다', '덧셈=이동, 뺄셈=부호 바꿔서 덧셈', '천천히 해도 맞으면 된다!'],
    practice: {
      q: '수직선을 머릿속에 그리며 계산해 보세요.\n① (-1) + (+6) = ?\n② (+3) - (+8) = ?\n③ (-4) - (-4) = ?',
      a: '① +5  ② -5  ③ 0',
      hint: '②는 (+3) + (-8)로, ③은 (-4) + (+4)로 바꿔보세요!',
      solutionSteps: [
        { label: '① (-1) + (+6)', content: '-1에서 오른쪽으로 6칸 이동: -1+6 = +5' },
        { label: '② (+3) - (+8)', content: '→ (+3) + (-8)\n+3에서 왼쪽으로 8칸: 3-8 = -5' },
        { label: '③ (-4) - (-4)', content: '→ (-4) + (+4)\n-4에서 오른쪽으로 4칸: -4+4 = 0' },
      ]
    },
    misconceptions: [
      { code: 'NM15', desc: '정수 계산이 너무 어렵다는 선입견 → 수직선만 그리면 모든 덧셈·뺄셈이 됩니다! 천천히!' },
    ],
  },
]

// ── 난이도 설정 ────────────────────────────────────────────────────────────────
const DIFFICULTY_CONFIG = {
  beginner:     { label: '기초', color: 'bg-green-100 text-green-700' },
  intermediate: { label: '보통', color: 'bg-yellow-100 text-yellow-700' },
  advanced:     { label: '심화', color: 'bg-red-100 text-red-700' },
}

// ── PracticeBox ────────────────────────────────────────────────────────────────
function PracticeBox({
  practice, stepId, unitId, alreadyAnswered, onAnswered
}: {
  practice: typeof STEPS[0]['practice']
  stepId: string
  unitId: string
  alreadyAnswered: boolean
  onAnswered: () => void
}) {
  const [show, setShow] = useState(false)
  const [revealed, setRevealed] = useState(alreadyAnswered)
  const [stepOpen, setStepOpen] = useState(false)

  return (
    <div className="card border-2 border-primary-100">
      <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
        <PenTool size={16} className="text-primary-500" />
        연습 문제
      </h3>
      <div className="bg-primary-50 rounded-xl p-4 mb-3">
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{practice.q}</p>
      </div>
      {!revealed ? (
        <div className="space-y-2">
          <button
            onClick={() => setShow(!show)}
            className="text-xs text-primary-500 hover:text-primary-700 flex items-center gap-1"
          >
            💡 힌트 {show ? '숨기기' : '보기'}
          </button>
          {show && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-yellow-800">
              {practice.hint}
            </div>
          )}
          <button
            onClick={() => { setRevealed(true); onAnswered() }}
            className="w-full py-2 rounded-xl bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors"
          >
            정답 확인
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
            <p className="text-sm font-bold text-slate-800 whitespace-pre-line">{practice.a}</p>
          </div>
          <button
            onClick={() => setStepOpen(!stepOpen)}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <ArrowRight size={10} className={`transition-transform ${stepOpen ? 'rotate-90' : ''}`} />
            {stepOpen ? '풀이 닫기' : '단계별 풀이 보기'}
          </button>
          {stepOpen && (
            <div className="space-y-2">
              {practice.solutionSteps.map((s, i) => (
                <div key={i} className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-xs font-bold text-slate-600 mb-0.5">{s.label}</p>
                  <p className="text-xs text-slate-700 whitespace-pre-line">{s.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── ExampleCard ────────────────────────────────────────────────────────────────
function ExampleCard({ ex, idx }: { ex: typeof STEPS[0]['examples'][0]; idx: number }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <div className="p-3">
        <p className="text-xs font-bold text-slate-400 mb-1">예시 {idx + 1}</p>
        <p className="text-sm text-slate-800 whitespace-pre-line">{ex.q}</p>
        <div className="mt-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
          <p className="text-sm font-bold text-primary-700">💡 {ex.a}</p>
        </div>
      </div>
      {ex.detail && (
        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left px-3 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 hover:bg-slate-100 transition-colors flex items-center gap-1"
        >
          <ArrowRight size={10} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          {open ? '풀이 숨기기' : '상세 풀이 보기'}
        </button>
      )}
      {open && ex.detail && (
        <div className="px-3 pb-3 bg-slate-50 text-xs text-slate-600 leading-relaxed whitespace-pre-line">
          {ex.detail}
        </div>
      )}
    </div>
  )
}

// ── 스토리카드 컴포넌트 ────────────────────────────────────────────────────────
function StoryCard({ stepId, color }: { stepId: string; color: typeof STEPS[0]['color'] }) {
  const story = STEP_STORIES[stepId]
  const [lineIdx, setLineIdx] = useState(0)
  const [finished, setFinished] = useState(false)

  if (!story) return null

  const currentLine = story.lines[lineIdx]
  const isLast = lineIdx === story.lines.length - 1

  const speakerColors: Record<string, string> = {
    teacher:  'from-indigo-500 to-blue-600',
    student:  'from-emerald-500 to-teal-600',
    friend:   'from-purple-500 to-pink-500',
    narrator: 'from-amber-400 to-orange-500',
  }
  const bgColors: Record<string, string> = {
    teacher:  'bg-indigo-50 border-indigo-200',
    student:  'bg-emerald-50 border-emerald-200',
    friend:   'bg-purple-50 border-purple-200',
    narrator: 'bg-amber-50 border-amber-200',
  }

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className={`bg-gradient-to-r ${color.border.replace('border-', 'from-').replace('-200', '-100')} to-white px-4 py-2.5 border-b border-slate-100 flex items-center gap-2`}>
        <MessageCircle size={13} className={color.accent} />
        <span className={`text-xs font-black ${color.accent}`}>📖 이야기로 시작하기</span>
        <span className="text-xs text-slate-400 ml-auto">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="px-4 pt-3 pb-1">
        <p className="text-xs text-slate-500 italic leading-relaxed">{story.scene}</p>
      </div>
      <div className="mx-4 mb-3 bg-slate-100 rounded-full h-1 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${speakerColors[currentLine.speaker]} rounded-full transition-all duration-500`}
          style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }}
        />
      </div>
      <div className="px-4 pb-3">
        <div className={`rounded-xl border ${bgColors[currentLine.speaker]} p-3 min-h-[80px] transition-all duration-300`}>
          {currentLine.speaker !== 'narrator' ? (
            <div className="flex items-start gap-2.5">
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${speakerColors[currentLine.speaker]} flex items-center justify-center text-lg flex-shrink-0 shadow-sm`}>
                {currentLine.emoji}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-500 mb-0.5">{currentLine.name}</p>
                <p className="text-sm text-slate-800 leading-relaxed">{currentLine.text}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <span className="text-lg">{currentLine.emoji}</span>
              <p className="text-sm text-amber-800 leading-relaxed italic flex-1">{currentLine.text}</p>
            </div>
          )}
        </div>
        {finished && (
          <div className={`mt-3 rounded-xl border-2 ${color.border} ${color.bg} px-4 py-3 flex items-center gap-2`}>
            <span className="text-xl">{story.revealEmoji}</span>
            <div>
              <p className="text-xs font-black text-slate-600 mb-0.5">✨ 수학 연결</p>
              <p className={`text-xs font-bold ${color.accent} leading-relaxed`}>{story.reveal}</p>
            </div>
          </div>
        )}
        <div className="flex gap-2 mt-3">
          {lineIdx > 0 && (
            <button
              onClick={() => { setLineIdx(i => i - 1); setFinished(false) }}
              className="text-xs px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
            >
              ← 이전
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setLineIdx(i => i + 1)}
              className={`flex-1 py-1.5 rounded-xl text-white text-xs font-bold ${color.btn} hover:opacity-90 transition-opacity flex items-center justify-center gap-1`}
            >
              <Play size={11} /> 다음 대화
            </button>
          ) : !finished ? (
            <button
              onClick={() => setFinished(true)}
              className="flex-1 py-1.5 rounded-xl text-white text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
            >
              <Sparkles size={11} /> 수학 연결 보기
            </button>
          ) : (
            <button
              onClick={() => { setLineIdx(0); setFinished(false) }}
              className="flex-1 py-1.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            >
              🔄 처음부터
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── 예상 파편 패널 ────────────────────────────────────────────────────────────
function PredictedFragmentPanel({
  fragments, onGoStep,
}: {
  fragments: PredictedFragment[]
  onGoStep: (stepId: string) => void
}) {
  const [open, setOpen] = useState(true)
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null

  const dangerCount  = visible.filter(f => f.tag === 'danger').length
  const warningCount = visible.filter(f => f.tag === 'warning').length

  return (
    <div className="rounded-2xl border-2 border-violet-200 bg-violet-50 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-violet-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-violet-600" />
          <span className="text-sm font-black text-violet-800">🤖 AI 예상 파편 분석</span>
          {dangerCount > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Flame size={10} /> 긴급 {dangerCount}
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">
              주의 {warningCount}
            </span>
          )}
        </div>
        <ChevronDown size={14} className={`text-violet-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-xs text-violet-600 mb-3">
            학습 기록을 분석해 <strong>틀릴 가능성이 높은 개념</strong>을 자동으로 추려냈습니다.
          </p>
          {visible.map(pred => {
            const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
            return (
              <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3 relative`}>
                <button
                  onClick={() => setDismissed(d => new Set([...d, pred.fragment.id]))}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
                <div className="flex items-start gap-2 pr-5">
                  <span className="text-xl">{pred.fragment.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>
                        {tagConf.label}
                      </span>
                      <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-800 leading-tight">{pred.fragment.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{pred.fragment.description}</p>
                    <div className="mt-1.5 space-y-0.5">
                      {pred.reason.map((r, i) => (
                        <p key={i} className="text-xs text-slate-500">{r}</p>
                      ))}
                    </div>
                    <div className="mt-2 bg-white/60 rounded-lg px-2 py-1.5">
                      <p className="text-xs font-semibold text-slate-700">
                        💡 <span className="font-bold">고치는 법:</span> {pred.fragment.fixTip}
                      </p>
                    </div>
                    <button
                      onClick={() => onGoStep(pred.fragment.stepId)}
                      className="mt-2 text-xs px-3 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700 transition-colors"
                    >
                      → {pred.fragment.stepTitle} 단계로 바로 가기
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── 수직선 인터랙티브 컴포넌트 ─────────────────────────────────────────────────
function NumberLineVisualizer() {
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [op, setOp] = useState<'+' | '-'>('+')
  const [result, setResult] = useState<number | null>(null)

  const calculate = () => {
    const numA = parseFloat(a)
    const numB = parseFloat(b)
    if (isNaN(numA) || isNaN(numB)) return
    setResult(op === '+' ? numA + numB : numA - numB)
  }

  const range = Array.from({ length: 21 }, (_, i) => i - 10)

  return (
    <div className="bg-white rounded-2xl border-2 border-blue-100 p-5 shadow-sm">
      <h3 className="font-black text-slate-800 mb-3 flex items-center gap-2">
        <span className="text-lg">↔️</span> 수직선 계산기
      </h3>
      <div className="flex gap-2 mb-3 flex-wrap">
        <input
          type="number" value={a} onChange={e => setA(e.target.value)}
          placeholder="첫 번째 수 (-10~10)"
          className="flex-1 min-w-[100px] border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-mono focus:border-blue-400 focus:outline-none"
        />
        <select
          value={op} onChange={e => setOp(e.target.value as '+' | '-')}
          className="border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-700 focus:border-blue-400 focus:outline-none"
        >
          <option value="+">+</option>
          <option value="-">−</option>
        </select>
        <input
          type="number" value={b} onChange={e => setB(e.target.value)}
          placeholder="두 번째 수"
          className="flex-1 min-w-[100px] border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-mono focus:border-blue-400 focus:outline-none"
        />
        <button
          onClick={calculate}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-black transition-colors"
        >계산</button>
      </div>

      {/* 수직선 시각화 */}
      <div className="overflow-x-auto pb-2">
        <div className="flex items-center gap-0 min-w-max mx-auto">
          <span className="text-slate-400 text-sm mr-1">←</span>
          {range.map(n => {
            const numA = parseFloat(a)
            const numB = parseFloat(b)
            const isStart = Math.round(numA) === n && !isNaN(numA)
            const isResult = result !== null && Math.round(result) === n
            return (
              <div key={n} className="flex flex-col items-center w-8">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  isResult ? 'bg-emerald-500 text-white ring-2 ring-emerald-300 scale-110' :
                  isStart ? 'bg-blue-500 text-white ring-2 ring-blue-300' :
                  n === 0 ? 'bg-slate-300 text-slate-600' :
                  'bg-slate-100 text-slate-400'
                }`}>
                  {n}
                </div>
                <div className="w-px h-2 bg-slate-200 mt-0.5" />
              </div>
            )
          })}
          <span className="text-slate-400 text-sm ml-1">→</span>
        </div>
      </div>

      {result !== null && (
        <div className="mt-3 bg-gradient-to-r from-blue-50 to-emerald-50 border-2 border-emerald-200 rounded-xl p-3">
          <p className="text-base font-black text-emerald-700 font-mono text-center">
            {a} {op} {b} = {result}
          </p>
          {op === '-' && (
            <p className="text-xs text-center text-slate-500 mt-1">
              뺄셈 변환: {a} + ({op === '-' ? `(${-parseFloat(b)})` : b}) = {result}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
const UNIT_ID = 'N1-2'

export default function N2LearnPage({ defaultStepId }: { defaultStepId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(defaultStepId ?? null)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const { completedCount, steps: stepProgress, pct } = useLearnProgress(UNIT_ID)
  const { completeStep, init } = useProgressStore(s => ({ completeStep: s.completeStep, init: s.init }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 4)

  useEffect(() => { init('student_001') }, [])

  useEffect(() => {
    if (defaultStepId) {
      setSelectedId(defaultStepId)
      setTimeout(() => {
        const el = document.getElementById(`step-${defaultStepId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }, [defaultStepId])

  const selected = STEPS.find(s => s.id === selectedId) ?? null
  const selectedIdx = STEPS.findIndex(s => s.id === selectedId)

  const isStepCompleted = (id: string) =>
    stepProgress[id]?.status === 'completed' || stepProgress[id]?.status === 'mastered'
  const isStepPracticed = (id: string) => stepProgress[id]?.practiceAnswered ?? false

  const handleComplete = (stepId: string) => {
    const step = STEPS.find(s => s.id === stepId)
    completeStep(UNIT_ID, stepId, step?.title)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <span>중1</span>
          <ChevronRight size={12} />
          <span>수와 연산</span>
          <ChevronRight size={12} />
          <span className="font-semibold text-slate-700">정수와 유리수</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Minus size={22} className="text-blue-500" />
          <Plus size={22} className="text-blue-500 -ml-3" />
          정수와 유리수 (N1-2)
        </h1>
        <p className="text-sm text-slate-500 mt-1">음수의 탄생부터 유리수 사칙연산까지 — 수의 세계를 완전히 확장한다</p>

        {/* 진도 바 */}
        <div className="mt-4 bg-slate-100 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-slate-500">{completedCount}/{STEPS.length} 단계 완료</p>
          <p className="text-xs font-bold text-blue-600">{pct}%</p>
        </div>
      </div>

      {/* 수포자 탈출 배너 */}
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-400 p-4 text-white flex items-center gap-3">
        <span className="text-3xl">🌱</span>
        <div>
          <p className="font-black text-sm">수포자 탈출 코너 — NT08 단계를 먼저 보세요!</p>
          <p className="text-xs opacity-90 mt-0.5">음수가 처음이거나 헷갈린다면, NT08 "처음부터" 단계에서 수직선 하나로 시작하세요!</p>
        </div>
        <button
          onClick={() => setSelectedId('NT08')}
          className="ml-auto flex-shrink-0 bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-colors"
        >
          시작 →
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Step List */}
        <div className="lg:col-span-1 space-y-2">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">학습 단계</h2>
          {STEPS.map(step => {
            const completed = isStepCompleted(step.id)
            const practiced = isStepPracticed(step.id)
            const isActive = step.id === selectedId
            const diffConf = DIFFICULTY_CONFIG[step.difficulty]

            return (
              <button
                key={step.id}
                id={`step-${step.id}`}
                onClick={() => setSelectedId(isActive ? null : step.id)}
                className={`w-full text-left rounded-xl border-2 p-3 transition-all ${
                  isActive
                    ? `${step.color.border} ${step.color.bg} shadow-sm`
                    : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${
                    completed ? 'bg-emerald-100' : step.color.bg
                  }`}>
                    {completed ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-bold ${step.color.accent}`}>{step.id}</span>
                      {step.id === 'NT08' && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-bold">수포자</span>
                      )}
                    </div>
                    <p className={`text-sm font-semibold truncate ${isActive ? 'text-slate-800' : 'text-slate-700'}`}>
                      {step.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${diffConf.color}`}>
                        {diffConf.label}
                      </span>
                      {practiced && !completed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-600">연습완료</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={14}
                    className={`flex-shrink-0 transition-transform ${isActive ? 'rotate-90 ' + step.color.accent : 'text-slate-300'}`}
                  />
                </div>
              </button>
            )
          })}

          {/* 예상 파편 패널 */}
          {predictedFragments.length > 0 && (
            <div className="mt-4">
              <PredictedFragmentPanel
                fragments={predictedFragments}
                onGoStep={(stepId) => setSelectedId(stepId)}
              />
            </div>
          )}
        </div>

        {/* Step Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="space-y-4">
              {/* 수직선 계산기 (선택 전 기본 표시) */}
              <NumberLineVisualizer />

              <div className="card flex flex-col items-center justify-center py-12 text-center">
                <div className="text-6xl mb-4">↔️</div>
                <p className="text-lg font-bold text-slate-600 mb-1">학습 단계를 선택하세요</p>
                <p className="text-sm text-slate-400">왼쪽 목록에서 원하는 단계를 클릭하면<br />개념 설명과 예시, 연습 문제가 나타납니다.</p>
                {completedCount > 0 && (
                  <div className="mt-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <p className="text-sm font-semibold text-emerald-700">
                      🎉 {completedCount}단계 완료! 계속 학습하세요.
                    </p>
                  </div>
                )}
                {predictedFragments.length > 0 && (
                  <div className="mt-5 w-full max-w-sm">
                    <p className="text-xs font-bold text-violet-600 mb-2 flex items-center justify-center gap-1">
                      <TrendingUp size={12} /> AI 추천 오늘 먼저 볼 단계
                    </p>
                    <div className="space-y-1.5">
                      {predictedFragments.slice(0, 3).map(pred => (
                        <button
                          key={pred.fragment.id}
                          onClick={() => setSelectedId(pred.fragment.stepId)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-50 border border-violet-200 hover:bg-violet-100 transition-colors text-left"
                        >
                          <span>{pred.fragment.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-700 truncate">{pred.fragment.stepTitle}</p>
                            <p className="text-xs text-violet-600 truncate">{pred.fragment.title}</p>
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 ${FRAGMENT_TAG_CONFIG[pred.tag].badge}`}>
                            {pred.tag === 'danger' ? '긴급' : pred.tag === 'warning' ? '주의' : '추천'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {STEPS.slice(0, 4).map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedId(s.id)}
                      className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600"
                    >
                      {s.icon} {s.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Card */}
              <div className={`rounded-2xl border-2 ${selected.color.border} ${selected.color.bg} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{selected.icon}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selected.color.badge}`}>{selected.id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_CONFIG[selected.difficulty].color}`}>
                          {DIFFICULTY_CONFIG[selected.difficulty].label}
                        </span>
                        {selected.id === 'NT08' && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">🌱 수포자 탈출</span>
                        )}
                        {isStepCompleted(selected.id) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle size={10} /> 완료
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-black text-slate-800">{selected.title}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">{selected.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-white/70 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                    <Target size={11} /> 학습 목표
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selected.objective}</p>
                </div>
                {selected.teaches.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selected.teaches.map(c => (
                      <span key={c} className={`text-xs px-2 py-0.5 rounded-full font-mono font-medium ${selected.color.badge}`}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* 스토리텔링 인트로 */}
              {STEP_STORIES[selected.id] && (
                <StoryCard stepId={selected.id} color={selected.color} />
              )}

              {/* NT01 — 수직선 계산기 삽입 */}
              {selected.id === 'NT01' && <NumberLineVisualizer />}

              {/* Concept */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Lightbulb size={16} className="text-yellow-500" />
                    개념 설명
                  </h3>
                  {(() => {
                    const approved   = getApprovedByTag(selected.id)
                    const recruiting = getRecruitingByTag(selected.id)
                    if (approved.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm"
                          title={`승인된 영상 ${approved.length}개`}
                        >
                          <Youtube size={12} />영상으로 학습 ({approved.length})
                        </button>
                      )
                    }
                    if (recruiting.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-violet-100 hover:bg-violet-200 text-violet-700 border border-violet-300"
                        >
                          <Youtube size={12} />🎬 영상 모집 중
                        </button>
                      )
                    }
                    return (
                      <button
                        onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-slate-100 hover:bg-slate-200 text-slate-400 border border-slate-200"
                      >
                        <Youtube size={12} />영상 학습
                      </button>
                    )
                  })()}
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
                    {selected.concept}
                  </pre>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.keyPoints.map(p => (
                    <span key={p} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                      <Star size={9} /> {p}
                    </span>
                  ))}
                </div>
              </div>

              {/* Examples */}
              <div className="card">
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Calculator size={16} className="text-blue-500" />
                  예시 문제
                </h3>
                <div className="space-y-3">
                  {selected.examples.map((ex, i) => (
                    <ExampleCard key={i} ex={ex} idx={i} />
                  ))}
                </div>
              </div>

              {/* Practice */}
              <PracticeBox
                practice={selected.practice}
                stepId={selected.id}
                unitId={UNIT_ID}
                alreadyAnswered={isStepPracticed(selected.id)}
                onAnswered={() => {}}
              />

              {/* Misconceptions */}
              {selected.misconceptions.length > 0 && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                  <h3 className="font-bold text-red-700 text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle size={14} /> 자주 하는 실수 (오개념 주의!)
                  </h3>
                  <div className="space-y-2">
                    {selected.misconceptions.map(m => (
                      <div key={m.code} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-red-100">
                        <span className="text-xs font-black text-red-500 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">{m.code}</span>
                        <span className="text-xs text-slate-700">{m.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 단계 완료 버튼 */}
              {!isStepCompleted(selected.id) ? (
                <button
                  onClick={() => handleComplete(selected.id)}
                  className={`w-full py-3 rounded-2xl text-white font-black text-sm flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${selected.color.btn}`}
                >
                  <CheckCircle size={16} /> 이 단계 학습 완료!
                </button>
              ) : (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3 flex items-center gap-3">
                  <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-black text-emerald-700">완료된 단계입니다 🎉</p>
                    <p className="text-xs text-emerald-500">완료일: {stepProgress[selected.id]?.completedAt?.slice(0,10) ?? ''}</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-2">
                {selectedIdx > 0 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx - 1].id)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                  >
                    ← 이전 단계
                  </button>
                ) : <span />}
                {selectedIdx < STEPS.length - 1 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx + 1].id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all ${selected.color.btn}`}
                  >
                    다음 단계 <ArrowRight size={14} />
                  </button>
                ) : (
                  <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle size={14} /> 전 과정 완료!
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
